// Which files a deck will take (P2.5) — FR-01's format list, and the ones it must refuse well.
//
// Pure string handling, no browser API, so the decisions are testable without a decoder. That
// matters more than it sounds: **the browser is not a reliable oracle for what we support.** Chrome
// decodes Ogg Vorbis perfectly happily, and Safari does not — so leaning on `decodeAudioData` to
// tell us what is supported would make SYNAPSE's format list a property of the user's browser
// rather than a property of the product. FR-01 names four formats; this file names the same four
// everywhere.
//
// FR-42's posture applies to every refusal here: a file we will not take gets an explanation, not
// a broken deck. UX §8 even writes the sentence — "Couldn't read this file. Try MP3, WAV, FLAC, or
// AAC." — and that copy is used verbatim rather than paraphrased.

/**
 * FR-01: "MP3/WAV/AAC/FLAC". The extensions those actually arrive with.
 *
 * ⚠️ **Documentation, not a filter, since 9 September.** It used to drive the picker's `accept`
 * attribute, which is how it came to hide a user's music folder — see `PICKER_ACCEPTS_EVERYTHING`.
 * Nothing gates on this list any more: `verifyFormat` refuses only what it can *explain*, and this
 * records which four formats FR-01 promises so a reader can find them without grepping the PRD.
 */
export const SUPPORTED_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.wave',
  '.flac',
  '.m4a',
  '.aac',
  '.mp4',
] as const;

/**
 * Formats we deliberately refuse, so the refusal can say *why*.
 *
 * Stack §5 marks OGG `[C]` — it needs a WASM decoder we have not built, and P2.5's rules say it is
 * out of scope and "should fail legibly rather than mysteriously". Legibly means naming the format
 * and saying it is not supported *yet*, rather than letting `decodeAudioData` throw
 * `EncodingError: Unable to decode audio data` — which is true, useless, and on Chrome would not
 * even happen, giving one user a working Ogg file and another a cryptic failure on the same build.
 */
const KNOWN_UNSUPPORTED = [
  { extension: '.ogg', label: 'Ogg Vorbis' },
  { extension: '.oga', label: 'Ogg Vorbis' },
  { extension: '.opus', label: 'Opus' },
  { extension: '.wma', label: 'Windows Media Audio' },
  { extension: '.aiff', label: 'AIFF' },
  { extension: '.aif', label: 'AIFF' },
] as const;

/**
 * ⚠️ **There is no `accept` filter on the picker, and that is a fix rather than an omission.**
 *
 * Reported by the stakeholder on 9 September: *"when I click on load a track and go to the music
 * folder it doesn't show there, so I have to drag and drop it there then it works."* Their file was
 * named `.mpeg`, which Chrome decodes perfectly well.
 *
 * The cause is two paths disagreeing about one rule — this repo's most-repeated bug shape, and the
 * seventh instance of it. `verifyFormat` below is deliberately **permissive**, and says so: *"An
 * unknown extension is accepted, not refused… The decode is the real test."* The picker's `accept`
 * attribute was a **closed list of seven extensions**. So dropping a file used the permissive rule
 * and worked; picking one used the restrictive rule and hid the user's entire music folder.
 *
 * The old rule was *"do not offer what you would then refuse"*, which sounds right and is wrong in
 * both directions:
 *
 *  1. It hides files that would have **worked** — `.mpeg` here, and anything else Chrome can decode
 *     that nobody thought to list. The user cannot act on a file they cannot see.
 *  2. It hides files whose refusal is **useful**. `KNOWN_UNSUPPORTED` exists so an Ogg gets "Ogg
 *     Vorbis isn't supported yet. Try MP3, WAV, FLAC, or AAC." Filtering Oggs out of the dialog
 *     means that sentence never runs, and the user sees an empty folder instead of an explanation.
 *
 * A MIME-type hint of the `audio` wildcard kind was considered and rejected: Windows registers `.mpeg` as
 * a **video** type rather than an audio one, so it would not have fixed the reported case, and
 * picking a filter whose behaviour depends on the user's OS registry is how this bug arrives
 * again wearing a different extension.
 *
 * **The rule now is one rule: the picker offers what `verifyFormat` accepts, which is everything.**
 * The cost is a dialog that also lists documents, which is a smaller cost than a dialog that lists
 * nothing. `tests/e2e/load-and-play.spec.ts` pins the absence of the filter, because an attribute on
 * an input is a browser fact and belongs in a browser test.
 */
export const PICKER_ACCEPTS_EVERYTHING = true;

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase();
}

export type FormatVerdict =
  { readonly accepted: true } | { readonly accepted: false; readonly reason: string };

/**
 * Decides whether to even attempt a decode.
 *
 * Runs on the extension rather than the MIME type, and that is not laziness. `File.type` is
 * whatever the operating system guessed: it is routinely `''` for a FLAC on Windows, and
 * `audio/mpeg` for things that are not MP3. The extension is what the user actually named the
 * file, which is the more honest signal *and* the one they can act on when told about it.
 *
 * An unknown extension is **accepted**, not refused. A file called `mixdown` with no extension at
 * all may well be a perfectly good WAV, and refusing it on a naming convention would be the
 * product being pedantic at the user's expense. The decode is the real test; this only catches the
 * cases where we can say something more useful than the decoder would.
 */
export function verifyFormat(fileName: string): FormatVerdict {
  const extension = extensionOf(fileName);

  const unsupported = KNOWN_UNSUPPORTED.find((entry) => entry.extension === extension);
  if (unsupported !== undefined) {
    return {
      accepted: false,
      reason:
        `${unsupported.label} isn't supported yet. ` + 'Try MP3, WAV, FLAC, or AAC.',
    };
  }

  return { accepted: true };
}

/**
 * What to show when a decode throws.
 *
 * UX §8's copy, verbatim: "Couldn't read this file. Try MP3, WAV, FLAC, or AAC." The underlying
 * error is deliberately **not** shown. `EncodingError: Unable to decode audio data` tells a user
 * nothing they can act on, and the one thing it might contain that is worth protecting is the
 * filename — FR-44 forbids that leaving the machine, and the habit of not putting it in UI strings
 * is what keeps it out of a screenshot in a bug report too.
 *
 * The real error still reaches the console for whoever is debugging.
 */
export function decodeFailureMessage(): string {
  return "Couldn't read this file. Try MP3, WAV, FLAC, or AAC.";
}

/**
 * Does this drag carry a file at all?
 *
 * `dataTransfer.types` is the only thing readable during `dragover` — the browser hides the actual
 * items until drop, on purpose, so a page cannot inventory what you are dragging over it. So the
 * outline can only promise "this is a file", never "this is an MP3", and offering a deck-coloured
 * welcome to a dragged folder is better than offering nothing to a valid track.
 */
export function dragCarriesFile(types: readonly string[]): boolean {
  return types.includes('Files');
}
